import React, { useState, useEffect, useMemo } from "react";
import dayjs from 'dayjs';

// MUI Components
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Grid,
    Divider,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Tooltip,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Draggable from 'react-draggable';

// Date & Time Pickers
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';

// Services
import ActionService from "../services/ActionService";
import AziendaService from "../services/AziendeService";

function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

const Actions = props => {
    // --- Costanti e Stati ---
    const simpleTypes = useMemo(() => ['IR', 'OR', 'VOL', 'WESE', 'WOD', 'SPORT', 'JS'], []);
    const settori = useMemo(() => ["Ufficio", "Ristorazione", "Manifattura", "Servizi", "Negozio", "Pulizie", "Logistica", "Sport", "Socio-sanitario", "Magazziniere", "Giardinaggio"], []);
    const esiti = useMemo(() => ["Positivo", "Negativo"], []);
    const tipiInserimento = useMemo(() => ["Lavorativo", "Di transizione"], []);
    const sportDisponibili = useMemo(() => ["Calcio", "Basket", "Nuoto", "Palestra", "Tennis", "Pallavolo", "Corsa", "Yoga", "Altro"], []);

    const tipologie = useMemo(() => [
        { sigla: 'IR', descrizione: 'Chiamate di soci in entrata' },
        { sigla: 'OR', descrizione: 'Chiamate ai soci in uscita' },
        { sigla: 'JS', descrizione: 'Lavoro in Job Station' },
        { sigla: 'VOL', descrizione: 'Volontariato di volontari o di soci' },
        { sigla: 'WESE', descrizione: 'Weekend, vacanze, serali' },
        { sigla: 'WOD', descrizione: 'Work Order Day' },
        { sigla: 'SPORT', descrizione: 'Attività sportiva' },
        { sigla: 'FORMAZIONE', descrizione: 'Corso di formazione' },
        { sigla: 'TE', descrizione: 'Sperimentazioni di lavoro retribuito temporanee' },
        { sigla: 'SE', descrizione: 'Lavoro esterno retribuito, supportato' },
        { sigla: 'IE', descrizione: 'Lavoro indipendente' },
        { sigla: 'JS-TE', descrizione: 'Job Station - Sperimentazioni' },
        { sigla: 'JS-SE', descrizione: 'Job Station - Lavoro Esterno' },
        { sigla: 'JS-IE', descrizione: 'Job Station - Lavoro Indipendente' }
    ], []);

    const isAdmin = props.user?.roles?.includes("ROLE_ADMIN") || props.user?.roles?.includes("ROLE_DIRETTORE") || false;
    const getInitialActionState = () => ({
        data: null, orario_inizio: null, orario_fine: null,
        tipologia: "", note: "",
        contatto_id: props.stakeholder.id, nome: props.stakeholder.nome, cognome: props.stakeholder.cognome,
        categoria_sh: props.stakeholder.categoria,
        azienda: "", mansione: "", tipo_contratto: "",
        data_inizio: null, data_fine: null,
        tipo_inserimento: "", esito: "", tutoraggio: "", tutor: "", settore: "", socio: "",
        sport_praticato: "", certificato_rilasciato: null,
    });

    const [action, setAction] = useState(getInitialActionState());
    const [rows, setRows] = useState([]);
    const [aziende, setAziende] = useState([]);
    const [open, setOpen] = useState(false);
    const [isNewAction, setIsNewAction] = useState(false);
    const [startTime, setStartTime] = useState(dayjs());
    const [endTime, setEndTime] = useState(dayjs());
    const [viewMode, setViewMode] = useState('simple');

    // --- Funzioni di Data Fetching ---
    const getAllActions = () => {
        if (!props.stakeholder?.id) return;
        ActionService.get(props.stakeholder.id)
            .then(response => setRows(response.data))
            .catch(e => console.error("Errore nel caricamento delle azioni:", e));
    }

    const retrieveAziende = () => {
        AziendaService.getAll()
            .then(response => setAziende(response.data))
            .catch(e => console.error("Errore nel caricamento delle aziende:", e));
    };

    useEffect(() => {
        getAllActions();
        retrieveAziende();
    }, [props.stakeholder.id]);

    // --- Gestori di Eventi ---
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setAction(prev => ({ ...prev, [name]: value }));
    };

    const handleRadioChange = (event) => {
        const { name, value } = event.target;
        setAction(prev => ({ ...prev, [name]: value === 'true' }));
    };

    const handleDateChange = (name, newValue) => {
        setAction(prev => ({ ...prev, [name]: newValue ? newValue.toDate() : null }));
    };

    const handleNewAction = () => {
        setAction(getInitialActionState());
        setStartTime(dayjs());
        setEndTime(dayjs());
        setIsNewAction(true);
        setOpen(true);
    }

    const handleRowClick = (params) => {
        const actionData = {
            ...getInitialActionState(),
            ...params.row,
            azienda: params.row.azienda_id || "",
        };
        setAction(actionData);
        setStartTime(params.row.orario_inizio ? dayjs(params.row.orario_inizio, "HH:mm:ss") : dayjs());
        setEndTime(params.row.orario_fine ? dayjs(params.row.orario_fine, "HH:mm:ss") : dayjs());
        setIsNewAction(false);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleViewChange = (event, newView) => {
        if (newView !== null) setViewMode(newView);
    };

    // --- Logica di Salvataggio e Cancellazione ---
    const saveData = () => {
        const isUpdate = !isNewAction;
        const commonData = {
            id: action.id, tipologia: action.tipologia, note: action.note,
            contatto_id: action.contatto_id, nome: action.nome, cognome: action.cognome,
            categoria_sh: Array.isArray(action.categoria_sh) ? action.categoria_sh.join(', ') : action.categoria_sh
        };

        let payload;
        if (simpleTypes.includes(action.tipologia)) {
            payload = {
                ...commonData,
                data: action.data,
                orario_inizio: startTime?.isValid() ? startTime.format("HH:mm:ss") : null,
                orario_fine: endTime?.isValid() ? endTime.format("HH:mm:ss") : null,
                sport_praticato: action.sport_praticato,
            };
        } else if (action.tipologia === 'FORMAZIONE') {
            payload = {
                ...commonData,
                data_inizio: action.data_inizio, data_fine: action.data_fine,
                azienda: action.azienda, certificato_rilasciato: action.certificato_rilasciato,
            };
        } else {
            payload = {
                ...commonData,
                azienda: action.azienda,
                mansione: action.mansione,
                tipo_contratto: action.tipo_contratto,
                data_inizio: action.data_inizio,
                data_fine: action.data_fine,
                tipo_inserimento: action.tipo_inserimento,
                esito: action.esito,
                tutoraggio: action.tutoraggio,
                tutor: action.tutor,
                settore: action.settore,
                socio: action.socio,
            };
        }

        const apiCall = isUpdate ? ActionService.update(action.id, payload) : ActionService.create(payload);
        apiCall.then(getAllActions).catch(e => console.error("Errore nel salvataggio:", e));
        handleClose();
    };

    const deleteAction = (id) => {
        if (window.confirm("Sei sicuro di voler eliminare questa azione?")) {
            ActionService.remove(id)
                .then(() => {
                    alert("Azione cancellata");
                    getAllActions();
                })
                .catch(e => console.error("Errore di cancellazione:", e));
            handleClose();
        }
    };

    // --- Colonne e Dati per DataGrid ---
    const countHours = (row) => {
        if (!row.orario_inizio || !row.orario_fine) return "00:00";
        const start = dayjs(row.orario_inizio, "HH:mm:ss");
        const end = dayjs(row.orario_fine, "HH:mm:ss");
        if (!start.isValid() || !end.isValid()) return "00:00";
        const diffMinutes = end.diff(start, 'minute');
        const h = Math.floor(diffMinutes / 60);
        const m = diffMinutes % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    const columns = useMemo(() => {
        const baseColumns = {
            simple: [
                { field: 'data', headerName: 'Data', type: "date", width: 110, valueGetter: params => params.value ? new Date(params.value) : null },
                { field: 'orario_inizio', headerName: 'Ora inizio', width: 100 },
                { field: 'orario_fine', headerName: 'Ora fine', width: 100 },
                { field: 'tot_ore', headerName: 'Totale ore', width: 100, valueGetter: (params) => countHours(params.row) },
                { field: 'tipologia', headerName: 'Tipologia', width: 120 },
                { field: 'sport_praticato', headerName: 'Sport', width: 130, valueGetter: params => params.row.tipologia === 'SPORT' ? params.value : '' },
                { field: 'note', headerName: 'Note', flex: 1, minWidth: 150 },
            ],
            complex: [
                { field: 'tipologia', headerName: 'Tipologia', width: 120 },
                {
                    field: 'azienda_id', headerName: 'Azienda', width: 200,
                    valueGetter: (params) => aziende.find(a => a.id === params.value)?.ragione_sociale || 'N/D'
                },
                { field: 'mansione', headerName: 'Mansione', width: 150 },
                { field: 'data_inizio', headerName: 'Data Inizio', type: 'date', width: 110, valueGetter: params => params.value ? new Date(params.value) : null },
                { field: 'data_fine', headerName: 'Data Fine', type: 'date', width: 110, valueGetter: params => params.value ? new Date(params.value) : null },
                { field: 'esito', headerName: 'Esito', width: 120 },
                { field: 'note', headerName: 'Note', flex: 1, minWidth: 150 },
            ],
            formazione: [
                { field: 'data_inizio', headerName: 'Data Inizio', type: 'date', width: 110, valueGetter: params => params.value ? new Date(params.value) : null },
                { field: 'data_fine', headerName: 'Data Fine', type: 'date', width: 110, valueGetter: params => params.value ? new Date(params.value) : null },
                {
                    field: 'azienda_id', headerName: 'Ente Certificatore', width: 200,
                    valueGetter: (params) => aziende.find(a => a.id === params.value)?.ragione_sociale || 'N/D'
                },
                { field: 'note', headerName: 'Descrizione / Note', flex: 1, minWidth: 200 },
                {
                    field: 'certificato_rilasciato', headerName: 'Certificato', width: 100,
                    valueGetter: params => params.value === true ? 'Sì' : params.value === false ? 'No' : ''
                },
            ]
        };
        return baseColumns[viewMode] || [];
    }, [viewMode, aziende]);

    const filteredRows = useMemo(() => {
        if (viewMode === 'simple') {
            return rows.filter(row => simpleTypes.includes(row.tipologia));
        } else if (viewMode === 'formazione') {
            return rows.filter(row => row.tipologia === 'FORMAZIONE');
        } else {
            return rows.filter(row => !simpleTypes.includes(row.tipologia) && row.tipologia !== 'FORMAZIONE');
        }
    }, [rows, viewMode, simpleTypes]);

    // --- Render Componente ---
    return (
        <Box sx={{ width: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Dialog open={open} onClose={handleClose} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title" maxWidth="md" fullWidth>
                    <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                        {isNewAction ? "Aggiungi Azione" : "Modifica Azione"}
                    </DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                            <InputLabel id="tipologia-label">Tipologia</InputLabel>
                            <Select labelId="tipologia-label" value={action.tipologia || ""} name="tipologia" onChange={handleInputChange} label="Tipologia">
                                {tipologie.map((tipo) => (
                                    <MenuItem value={tipo.sigla} key={tipo.sigla}>
                                        <Tooltip title={tipo.descrizione} placement="right" arrow>
                                            <span>{tipo.sigla}</span>
                                        </Tooltip>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {action.tipologia && (
                            simpleTypes.includes(action.tipologia) ? (
                                <>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
                                        <DatePicker label="Data" value={action.data ? dayjs(action.data) : null} onChange={(newValue) => handleDateChange('data', newValue)} />
                                        <TimeField label="Orario inizio" value={startTime} onChange={setStartTime} format="HH:mm" />
                                        <TimeField label="Orario fine" value={endTime} onChange={setEndTime} format="HH:mm" />
                                    </Box>
                                    {action.tipologia === 'SPORT' && (
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel>Sport</InputLabel>
                                            <Select name="sport_praticato" value={action.sport_praticato || ''} label="Sport" onChange={handleInputChange}>
                                                {sportDisponibili.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </>
                            ) : action.tipologia === 'FORMAZIONE' ? (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="h6" gutterBottom>Dettagli Formazione</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}><DatePicker label="Data Inizio" value={action.data_inizio ? dayjs(action.data_inizio) : null} onChange={(v) => handleDateChange('data_inizio', v)} sx={{ width: '100%' }} /></Grid>
                                        <Grid item xs={12} sm={6}><DatePicker label="Data Fine" value={action.data_fine ? dayjs(action.data_fine) : null} onChange={(v) => handleDateChange('data_fine', v)} sx={{ width: '100%' }} /></Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Ente Certificatore</InputLabel>
                                                <Select name="azienda" value={action.azienda || ''} label="Ente Certificatore" onChange={handleInputChange}>
                                                    {aziende.map((a) => (<MenuItem key={a.id} value={a.id}>{a.ragione_sociale}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend">Certificato Rilasciato</FormLabel>
                                                <RadioGroup row name="certificato_rilasciato" value={action.certificato_rilasciato ?? ''} onChange={handleRadioChange}>
                                                    <FormControlLabel value="true" control={<Radio />} label="Sì" />
                                                    <FormControlLabel value="false" control={<Radio />} label="No" />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="h6" gutterBottom>Dettagli Lavorativi</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}><FormControl fullWidth><InputLabel>Azienda</InputLabel><Select name="azienda" value={action.azienda || ''} label="Azienda" onChange={handleInputChange}>{aziende.map((a) => (<MenuItem key={a.id} value={a.id}>{a.ragione_sociale}</MenuItem>))}</Select></FormControl></Grid>
                                        <Grid item xs={12} sm={6}><TextField label="Mansione" name="mansione" fullWidth value={action.mansione || ''} onChange={handleInputChange} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField label="Tipo Contratto" name="tipo_contratto" fullWidth value={action.tipo_contratto || ''} onChange={handleInputChange} /></Grid>
                                        <Grid item xs={12} sm={6}><FormControl fullWidth><InputLabel>Settore</InputLabel><Select name="settore" value={action.settore || ''} label="Settore" onChange={handleInputChange}>{settori.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}</Select></FormControl></Grid>
                                    </Grid>
                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6" gutterBottom>Periodo</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}><DatePicker label="Data Inizio" value={action.data_inizio ? dayjs(action.data_inizio) : null} onChange={(v) => handleDateChange('data_inizio', v)} sx={{ width: '100%' }} /></Grid>
                                        <Grid item xs={12} sm={6}><DatePicker label="Data Fine" value={action.data_fine ? dayjs(action.data_fine) : null} onChange={(v) => handleDateChange('data_fine', v)} sx={{ width: '100%' }} /></Grid>
                                    </Grid>
                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6" gutterBottom>Esito e Supporto</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Tipo Inserimento</InputLabel>
                                                <Select name="tipo_inserimento" value={action.tipo_inserimento || ''} label="Tipo Inserimento" onChange={handleInputChange}>
                                                    {tipiInserimento.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Esito</InputLabel>
                                                <Select name="esito" value={action.esito || ''} label="Esito" onChange={handleInputChange}>
                                                    {esiti.map((e) => (<MenuItem key={e} value={e}>{e}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Tutoraggio" name="tutoraggio" fullWidth value={action.tutoraggio || ''} onChange={handleInputChange} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Tutor" name="tutor" fullWidth value={action.tutor || ''} onChange={handleInputChange} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Socio" name="socio" fullWidth value={action.socio || ''} onChange={handleInputChange} />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )
                        )}
                        {action.tipologia && (<TextField label="Note / Descrizione" name="note" fullWidth multiline rows={3} sx={{ mt: 3, mb: 1 }} value={action.note || ''} onChange={handleInputChange} />)}
                    </DialogContent>
                    <DialogActions>
                        <Button startIcon={<DeleteIcon />} color="error" disabled={isNewAction || !isAdmin} onClick={() => deleteAction(action.id)}>Cancella</Button>
                        <Button onClick={handleClose}>Chiudi</Button>
                        <Button onClick={saveData}>{isNewAction ? "Salva" : "Aggiorna"}</Button>
                    </DialogActions>
                </Dialog>

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewAction}>Aggiungi Azione</Button>
                    <ToggleButtonGroup color="primary" value={viewMode} exclusive onChange={handleViewChange} aria-label="tipologia di azioni">
                        <ToggleButton value="simple">Attività Club</ToggleButton>
                        <ToggleButton value="complex">Lavoro</ToggleButton>
                        <ToggleButton value="formazione">Formazione</ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="outlined" onClick={getAllActions} endIcon={<AutorenewIcon />}>Aggiorna</Button>
                </Box>

                <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        initialState={{
                            sorting: { sortModel: [{ field: viewMode === 'simple' ? 'data' : 'data_inizio', sort: 'desc' }] },
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        onRowClick={handleRowClick}
                    />
                </Box>
            </LocalizationProvider>
        </Box>
    );
};

export default Actions;